import { storeToRefs } from "pinia";
import { type WhereClause } from "spraypaint";

import { MediaRequest as MediaRequestResource } from "shared/resources";
import useUserStore from "shared/stores/user";
import {
  type MediaRequest,
  type MediaRequest as MentionMediaRequest,
} from "shared/types/mentions";

const TV_TYPES = ["TvChannel", "TvCaption"];

const DEFAULT_TVEYES_RADIO_IMAGE_URL = `${import.meta.env.VITE_ASSETS_URL}/images/radio-poster.png`;

function isTvEyesMediaRequest(mediaRequest: MentionMediaRequest) {
  return mediaRequest.origin_keyword === "TvEyes";
}

function mapFromResource(
  mediaRequestResource: MediaRequestResource
): MentionMediaRequest {
  return {
    id: Number(mediaRequestResource.id),
    status: mediaRequestResource.status,
    media_url: mediaRequestResource.mediaUrl,
    created_at: mediaRequestResource.createdAt,
    source_type: mediaRequestResource.sourceType,
    thumbnail_url: mediaRequestResource.thumbnailUrl,
    origin_keyword: mediaRequestResource.originKeyword,
    user: {
      name: mediaRequestResource.user.name,
    },
    instant_download: mediaRequestResource.instantDownload,
  };
}

function sourceType(mediaRequest: MentionMediaRequest): string {
  if (TV_TYPES.includes(mediaRequest.source_type)) return "video";

  return "audio";
}

function tvEyesMediaRequestThumbnailUrl(
  mediaRequest: MentionMediaRequest
): string {
  if (sourceType(mediaRequest) === "audio")
    return DEFAULT_TVEYES_RADIO_IMAGE_URL;

  return mediaRequest.thumbnail_url;
}

function isMediaRequestProcessed(
  mediaRequest: MediaRequestResource | MentionMediaRequest
) {
  return mediaRequest.status !== "In Progress";
}

async function fetchCurrentUserMediaRequests(
  query?: WhereClause
): Promise<Array<MediaRequest>> {
  const { currentUser } = storeToRefs(useUserStore());

  const response = await MediaRequestResource.includes(["user"])
    .select({ user: ["name"] })
    .where({ ...(query || {}), userId: currentUser.value.id })
    .all();

  return response.data
    ? response.data.map((resource) => mapFromResource(resource))
    : [];
}

export {
  fetchCurrentUserMediaRequests,
  isMediaRequestProcessed,
  isTvEyesMediaRequest,
  mapFromResource,
  sourceType,
  tvEyesMediaRequestThumbnailUrl,
};
